<template>
  <section v-if="visible" class="dc-car-template mb-4" data-testid="car-template-assign">
    <v-select v-model="selected" :items="items" :label="$t('damage_control.templates.assignment')"
              :disabled="!ready || busy" :loading="busy" :hint="assignmentHint" persistent-hint
              data-testid="car-template-select" @change="assign" />
    <v-alert v-if="errorText" type="warning" outlined dense role="alert">{{ errorText }}</v-alert>
  </section>
</template>

<script>
import { damageControlApi, damageControlEnabled, reportDamageControlFailure } from '@/api/damageControl';
import damageControlAccessMixin from '@/mixins/damageControlAccessMixin';
import roleMixin from '@/mixins/roleMixin';

export default {
  name: 'CarTemplateAssign',
  mixins: [roleMixin, damageControlAccessMixin],
  props: {
    car: { type: Object, required: true },
    templates: { type: Array, default: () => [] },
    ready: { type: Boolean, default: false },
  },
  data: () => ({ selected: undefined, confirmed: undefined, busy: false, errorText: '', sequence: 0, disposed: false }),
  computed: {
    company() { return this.damageControlCompany || {}; },
    visible() { return damageControlEnabled(this.company) && this.isManagerPlus; },
    contextKey() { return JSON.stringify([this.company.id, this.car.id, this.visible]); },
    items() { return [{ value: null, text: this.$t('damage_control.templates.default') }, ...this.templates.map(template => ({ value: template.id, text: template.name }))]; },
    assignmentHint() { return this.confirmed === undefined ? this.$t('damage_control.templates.current_unknown') : this.$t('damage_control.templates.assignment_hint'); },
  },
  watch: {
    contextKey: { immediate: true, handler() { this.sequence += 1; this.busy = false; this.errorText = ''; this.sync(); } },
    'car.damage_control_template_id'() { this.sync(); },
  },
  beforeDestroy() { this.disposed = true; this.sequence += 1; },
  methods: {
    sync() { this.selected = this.car.damage_control_template_id; this.confirmed = this.selected; },
    async assign(templateId) {
      if (!this.visible || !this.ready || this.busy || this.disposed || !this.car.id ||
          (templateId !== null && !this.templates.some(template => template.id === templateId))) return;
      const sequence = this.sequence, context = this.contextKey;
      const current = () => !this.disposed && this.visible && context === this.contextKey && sequence === this.sequence;
      this.busy = true; this.errorText = ''; this.selected = templateId;
      try {
        const data = await damageControlApi.assignTemplate(this.axios, this.car.id, templateId);
        if (!current()) return;
        this.selected = data.template_id; this.confirmed = data.template_id;
        this.$emit('assigned', data.template_id);
      } catch (error) {
        if (!current()) return;
        this.selected = this.confirmed;
        this.errorText = reportDamageControlFailure(this, error, { silent: true });
      } finally { if (current()) this.busy = false; }
    },
  },
};
</script>

<style scoped>
.dc-car-template { min-width: 0; max-width: 640px; }
.dc-car-template ::v-deep .v-select__selection { white-space: normal; overflow-wrap: anywhere; max-width: 100%; }
</style>
